import { useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { selectAtom } from "jotai/utils";
import React, { useEffect, useMemo, useRef } from "react";
import { useMenu } from "src/components/Menu";
import { useTranslation } from "src/i18n";

import { Transition } from "src/utils";
import { KeyCode } from "src/utils/keys";
import styled from "styled-components";

import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { planogramLegendAtom } from "src/components/PlanogramExtensions/PlanogramLegend";
import {
  labelOptionAtom,
  planogramHoveredItemAtom,
  planogramImagesModeAtom,
  planogramSelectedItemsAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { Color, Flex } from "src/elements";
import { usePogDeleteProductModal } from "src/modals/PogDeleteProduct/store/hooks";
import {
  baseHighlightsAtom,
  highlightsModeAtom,
  planogramIssueHighlightAtom,
  planogramIssueToneAtom,
  sideViewAtom,
} from "../../store/atoms";
import { OnItemHover, PogAtom } from "../../store/types";
import { areShelvesInSameMergedGroup, resolveDraggedItems } from "../../store/utils";
import { getCappingSpanCount } from "../../common/planogramFacing";
import { CdtStudioItemPin } from "src/pages/CdtStudio/components/CdtStudioItemPin";
import { Dropper } from "./Dropper";
import { Facing } from "./Facing";
import { OverlayWrapper } from "./OverlayWrapper";
import { getHighlightColor } from "./store/highlight";
import { onDragStart, onDropOverToItem } from "./store/utils";
import { PlanogramItem, UnrangedPlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { usePogAtomActions } from "../../store/actions";
import { MouseButton } from "src/types/mouseButtons";
import { usePlanogramPanel } from "../Panel/store/hooks";
import { DraggedItemsPayload } from "./store/types";
import { isPlanogramItemSelection } from "src/components/Planogram/common/planogramSelection";
import { resolvePlanogramProductLabel } from "src/components/Planogram/common/planogramItemLabel";
import { SelectionOutliner } from "./SelectionOutliner";
import { useFocusableElement } from "src/hooks";
import { resolveItemVisuals } from "./overlays/visualResolver";
import { getBetweenFacingsDividerWidth } from "@CommonUtils/pog/shelfDividers";
import { FrontOverhangBadge } from "../../common/FrontOverhangBadge";
import { itemFrontOverhangMarker } from "../../common/frontOverhangView";
import { useDrawnSqueeze } from "../../common/drawnSqueeze";

export { getCappingSpanCount } from "../../common/planogramFacing";

const ItemContainer = styled(Flex)<{ shelfType?: PlanogramShelf["type"] }>`
  align-items: ${({ shelfType }) => (shelfType === "HANGCELL" ? "flex-start" : "flex-end")};
  height: min-content;
  flex-grow: 1;

  &[data-placeholder] .planogram-item::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: calc(100% - 2px);
    height: calc(100% - 2px);
    background-color: ${Color.greenSmoke};
    border: 1px dashed ${Color.greenSmokeActive};
    border-radius: 2px;
  }

  &[data-draggable] {
    position: fixed;
    left: var(--xLeft);
    top: var(--yTop);

    &,
    * {
      pointer-events: none !important;
    }
  }
`;

const DropperContainer = styled(Flex)<{ shelfType?: PlanogramShelf["type"] }>`
  height: 0;
  flex-grow: 1;
  align-items: ${({ shelfType }) => (shelfType === "HANGCELL" ? "flex-start" : "flex-end")};
`;

const ItemsElement = styled(Flex)<{
  isEditable: boolean;
  isNotHighlighted: boolean;
  tabIndex?: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
  user-select: none;
  cursor: ${({ isEditable }) => (isEditable && "grab") || "default"};
  box-sizing: border-box;
  align-self: flex-end;
  pointer-events: ${({ isEditable }) => isEditable && "all"};
  border-radius: 3px;
  opacity: ${({ isNotHighlighted }) => isNotHighlighted && "0.5"};

  * {
    pointer-events: none;
  }
`;

const BaseGridElement = styled(Flex)<{
  item: PlanogramItem | UnrangedPlanogramItem;
  $isSupportingCapping?: boolean;
  /** Screen px between facing columns: the divider standing after each one. */
  $columnGap?: number;
}>`
  display: grid;
  grid-template-columns: ${({ item }) => `repeat(${item.getHorizontalFacings()}, auto)`};
  column-gap: ${({ $columnGap }) => ($columnGap ? `${$columnGap}px` : "0")};
  grid-template-rows: ${({ item }) => `repeat(${item.getVerticalFacings()}, auto)`};
  grid-auto-flow: column;
  box-sizing: border-box;
  background-color: ${({ $isSupportingCapping }) => $isSupportingCapping && Color.purple100};
  outline: ${({ $isSupportingCapping }) => $isSupportingCapping && "1px solid rgba(0, 0, 0, 0.22)"};
  outline-offset: -1px;
`;

const VerticalCappingContainer = styled(Flex)<{ $columns: number; $rows: number }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => `repeat(${$columns}, auto)`};
  grid-template-rows: ${({ $rows }) => `repeat(${$rows}, auto)`};
  grid-auto-flow: column;
  align-items: flex-start;
  justify-content: flex-start;
`;

const MainRowContainer = styled(Flex)`
  flex-direction: row;
  align-items: flex-end;
`;

const HorizontalCappingContainer = styled(Flex)<{ $columns: number; $rows: number }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => `repeat(${$columns}, auto)`};
  grid-template-rows: ${({ $rows }) => `repeat(${$rows}, auto)`};
  grid-auto-flow: column;
  align-items: flex-start;
  align-self: flex-end;
`;

const Tray = styled(Flex)`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  background-color: ${Color.gray500};
  border: 1px solid ${Color.gray800};
`;

const Highlighter = styled(Flex)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  border: 1px solid rgba(0, 0, 0, 0.9);
  background: yellow;
  border-radius: 3px;
  animation: pulsate infinite ${Transition.turtle};

  @keyframes pulsate {
    0% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.8;
    }
    100% {
      opacity: 0.4;
    }
  }
`;

/* The review's issue and changed-product marks, painted as the canvas paints them: no pulse. */
const ToneHighlighter = styled.div<{ $color: string; $outline: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  border: 2px solid ${({ $color }) => $color};
  border-radius: 3px;
  background: ${({ $color, $outline }) => ($outline ? "transparent" : $color)};
  opacity: ${({ $outline }) => ($outline ? 1 : 0.5)};
`;

const Highlight = styled(Flex)<{ highlightColor: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ highlightColor }) => highlightColor};
  opacity: 0.5;
`;

interface Props {
  currentStoreCode?: string;
  item: PlanogramItem | UnrangedPlanogramItem;
  scaleX: number;
  scaleY: number;
  isHighlighted?: boolean;
  isScrolledToView?: boolean;
  isEditable: boolean;
  pogAtom: PogAtom;
  refItemContainer?: React.RefObject<HTMLDivElement | null>;
  onItemHover?: OnItemHover;
  setIsModified: (modified: boolean) => void;
  availableStores: ResponseMerchFlowApplyFilters["filter_config"] | null;
  pdfMode?: boolean;
  customItemLabelOnly?: boolean;
  customLabelRule?: (
    item: PlanogramItem | UnrangedPlanogramItem,
    t?: (key: string, options?: Record<string, string | number>) => string,
  ) => string | null | undefined;
  /**
   * On-screen (non-PDF) label that replaces the label option for an item, drawn on the product
   * image. Null or undefined for an item falls back to the normal label.
   */
  canvasItemLabelRule?: CanvasItemLabelRule;
}

export type CanvasItemLabelRule = (
  item: PlanogramItem | UnrangedPlanogramItem,
) => string | null | undefined;

const ItemInner = (props: Props) => {
  const {
    item,
    scaleX,
    scaleY,
    isEditable,
    isHighlighted,
    isScrolledToView,
    pogAtom,
    refItemContainer: forwardedRefItemContainer,
    setIsModified,
    availableStores,
    pdfMode,
    customItemLabelOnly,
    customLabelRule,
    canvasItemLabelRule,
  } = props;
  const { t } = useTranslation("common");
  const pog = useAtomValue(pogAtom);
  const {
    addItemNextToAnotherItem,
    duplicateItemsNextToItem,
    flipItemsHorizontally,
    unrangeItems,
  } = usePogAtomActions(pogAtom);
  const { openPogDeleteProductModal } = usePogDeleteProductModal();
  const { openMenu } = useMenu();
  const { setTab } = usePlanogramPanel();

  const setHoveredItem = useSetAtom(planogramHoveredItemAtom);
  const [highlightsMode] = useAtom(highlightsModeAtom);
  const issueTone = useAtomValue(planogramIssueToneAtom);
  const issueHighlight = useAtomValue(planogramIssueHighlightAtom);
  const tone =
    isHighlighted && issueTone && issueHighlight.includes(item.uniqueId) ? issueTone : null;
  const [baseHighlights] = useAtom(baseHighlightsAtom);
  // Do not subscribe selection on every Item — handlers read the store at event time.
  const store = useStore();
  const setSelectedItems = useSetAtom(planogramSelectedItemsAtom);
  const currentShelfSelection = () =>
    store.get(planogramSelectedItemsAtom).filter(isPlanogramItemSelection);
  const isSelected = useAtomValue(
    useMemo(
      () =>
        selectAtom(planogramSelectedItemsAtom, (selection) =>
          selection.some((entry) => entry === (item as unknown)),
        ),
      [item],
    ),
  );
  const planogramLegend = useAtomValue(planogramLegendAtom);

  const labelOption = useAtomValue(labelOptionAtom);
  const imageMode = useAtomValue(planogramImagesModeAtom);
  const setSideView = useSetAtom(sideViewAtom);

  const customLabelValue = useMemo(() => {
    return customLabelRule?.(item, t);
  }, [customLabelRule, item, t]);

  const isCustomItem = customLabelValue !== undefined && customLabelValue !== null;

  const canvasLabelValue = useMemo(
    () => (pdfMode ? null : (canvasItemLabelRule?.(item) ?? null)),
    [pdfMode, canvasItemLabelRule, item],
  );

  const renderMode = useMemo(() => {
    if (pdfMode) {
      if (customItemLabelOnly) {
        return isCustomItem ? "label-on-image" : "image-only";
      }
      return "label-on-image";
    }
    if (canvasLabelValue !== null) {
      return imageMode ? "label-on-image" : "label-only";
    }
    return imageMode ? "image-only" : "label-only";
  }, [pdfMode, customItemLabelOnly, imageMode, isCustomItem, canvasLabelValue]);

  const labelText = useMemo(() => {
    if (canvasLabelValue !== null) {
      return canvasLabelValue;
    }
    if (labelOption === "none") {
      return "";
    }
    if (renderMode === "label-on-image" && isCustomItem) {
      return customLabelValue ?? "";
    }
    return resolvePlanogramProductLabel(item, labelOption, t("na"));
  }, [canvasLabelValue, renderMode, isCustomItem, customLabelValue, item, labelOption, t]);

  const overlays = useMemo(() => {
    return resolveItemVisuals(item, {
      renderMode,
      labelText,
      isFixedSize: isCustomItem,
    });
  }, [item, renderMode, labelText, isCustomItem]);

  const refItemContainer = useRef<HTMLDivElement>(null);
  const refItem = useRef<HTMLDivElement>(null);
  const { focus: focusItem } = useFocusableElement<HTMLDivElement>(refItem);

  const isPlanogramItem = "shelf" in item;
  const drawn = useDrawnSqueeze();
  const isNewInsertedItem =
    item.updateStatus?.operation === "INSERT" && item.updateStatus?.changeType === "INSERTED";

  const highlight = useMemo(() => {
    if (!highlightsMode || !isPlanogramItem) return false;

    if (baseHighlights) {
      for (const highlight of baseHighlights.filter((highlight) => highlight.enabled)) {
        const highlightColor = getHighlightColor(highlight, item, pog, planogramLegend);
        if (highlightColor) {
          return highlightColor;
        }
      }
    }

    return false;
  }, [baseHighlights, highlightsMode, isPlanogramItem, item, planogramLegend, pog]);

  const getRef = () => forwardedRefItemContainer || refItemContainer;

  useEffect(() => {
    if (isHighlighted && isScrolledToView) {
      getRef().current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }, [isHighlighted, isScrolledToView]);

  const onPointerDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const selectedShelfItems = currentShelfSelection();
    const draggedItems =
      isPlanogramItem &&
      selectedShelfItems.some((selectedItem) => selectedItem.uniqueId === item.uniqueId)
        ? (pog?.planogram
            .getAllItems()
            .filter((planogramItem) =>
              selectedShelfItems.some(
                (selectedItem) => selectedItem.uniqueId === planogramItem.uniqueId,
              ),
            ) ?? [item])
        : [item];

    onDragStart({
      event,
      draggedItems: {
        anchorItemId: item.uniqueId,
        itemIds: draggedItems.map((draggedItem) => draggedItem.uniqueId),
        mode: isPlanogramItem && (event.ctrlKey || event.metaKey) ? "copy" : "move",
      },
      refItemContainer: getRef(),
      refItem,
      item,
      setSidepanelTab: setTab,
    });
  };

  const getDraggedItems = (payload: DraggedItemsPayload) => {
    if (!pog) {
      return [];
    }

    return resolveDraggedItems(pog, payload.itemIds);
  };

  const onDropOverLeft = () => {
    setIsModified(true);
    onDropOverToItem({
      refItemContainer: getRef(),
      side: "left",
      onDrop: onDropLeft,
    });
  };

  const onDropOverRight = () => {
    setIsModified(true);
    onDropOverToItem({
      refItemContainer: getRef(),
      side: "right",
      onDrop: onDropRight,
    });
  };

  const onDropLeft = (payload: DraggedItemsPayload) => {
    if (isPlanogramItem) {
      setIsModified(true);

      if (payload.mode === "copy") {
        duplicateItemsNextToItem(item, payload.itemIds, "left");
        return;
      }

      const draggedItems = getDraggedItems(payload);
      if (draggedItems.length > 0) {
        addItemNextToAnotherItem(item, draggedItems, "left");
      }
    }
  };

  const onDropRight = (payload: DraggedItemsPayload) => {
    if (isPlanogramItem) {
      setIsModified(true);

      if (payload.mode === "copy") {
        duplicateItemsNextToItem(item, payload.itemIds, "right");
        return;
      }

      const draggedItems = getDraggedItems(payload);
      if (draggedItems.length > 0) {
        addItemNextToAnotherItem(item, draggedItems, "right");
      }
    }
  };

  const onPointerEnter = () => {
    if (isPlanogramItem) {
      setHoveredItem(item);
    }
  };

  const onPointerLeave = () => {
    setHoveredItem(null);
  };

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== MouseButton.PRIMARY || !isPlanogramItem) return;

    focusItem();

    setSelectedItems((selectedItems) => {
      const selectedShelfItems = selectedItems.filter(isPlanogramItemSelection);

      if (event.ctrlKey || event.metaKey) {
        if (selectedShelfItems.includes(item)) {
          return selectedShelfItems.filter((selectedItem) => selectedItem !== item);
        } else {
          return [...selectedShelfItems, item];
        }
      } else if (event.shiftKey) {
        if (selectedShelfItems.length === 0) {
          return [item];
        } else if (selectedShelfItems[0] === item) {
          return [selectedShelfItems[0]];
        } else {
          const newSelectedItems = [];
          const shelfItems = pog.planogram.bays.flatMap((bay) =>
            bay.shelves.flatMap((shelf) => shelf.items),
          );
          // The range follows the products as drawn, squeezed only as much as their shelf needs.
          const drawnCoordinates = drawn(
            shelfItems,
            () => new Map(shelfItems.map((shelfItem) => [shelfItem, shelfItem.getCoordinates()])),
          );
          const coordinatesOf = (shelfItem: PlanogramItem) =>
            drawnCoordinates.get(shelfItem) ?? shelfItem.getCoordinates();

          const item1Coordinates = coordinatesOf(selectedShelfItems[0]);
          const item2Coordinates = coordinatesOf(item);
          const selectionCoordinates = {
            xLeft: Math.min(item1Coordinates.xLeft, item2Coordinates.xLeft),
            xRight: Math.max(item1Coordinates.xRight, item2Coordinates.xRight),
            yBottom: Math.min(item1Coordinates.yBottom, item2Coordinates.yBottom),
            yTop: Math.max(item1Coordinates.yTop, item2Coordinates.yTop),
          };

          for (const bay of pog.planogram.bays) {
            for (const shelf of bay.shelves) {
              for (const item of shelf.items) {
                const itemCoordinates = coordinatesOf(item);

                if (
                  ((itemCoordinates.xLeft >= selectionCoordinates.xLeft &&
                    itemCoordinates.xRight <= selectionCoordinates.xRight) ||
                    (itemCoordinates.xRight > selectionCoordinates.xLeft &&
                      itemCoordinates.xRight < selectionCoordinates.xRight) ||
                    (itemCoordinates.xRight > selectionCoordinates.xRight &&
                      itemCoordinates.xLeft < selectionCoordinates.xRight)) &&
                  ((itemCoordinates.yBottom >= selectionCoordinates.yBottom &&
                    itemCoordinates.yTop <= selectionCoordinates.yTop) ||
                    (itemCoordinates.yTop > selectionCoordinates.yTop &&
                      itemCoordinates.yBottom < selectionCoordinates.yTop) ||
                    (itemCoordinates.yBottom < selectionCoordinates.yBottom &&
                      itemCoordinates.yTop > selectionCoordinates.yBottom))
                ) {
                  newSelectedItems.push(item);
                }
              }
            }
          }

          return newSelectedItems;
        }
      } else {
        return [item];
      }
    });

    setTab("PRODUCT_DETAILS");
  };

  const openContextMenu = (event: React.MouseEvent<HTMLDivElement>, items: PlanogramItem[]) => {
    const canFlipHorizontally =
      items.length > 1 && areShelvesInSameMergedGroup(items.map((item) => item.shelf));

    focusItem();

    openMenu({
      event,
      menu: {
        title: items.length > 1 ? `${items.length} items` : items[0].productCode,
        options: [
          {
            label: t("planogramCanvas.itemMenu.duplicate"),
            keybind: { keyCode: KeyCode.D },
            onClick: () => {
              setIsModified(true);
              duplicateItemsNextToItem(
                items[0],
                items.map((selected) => selected.uniqueId),
                "right",
              );
            },
          },
          {
            label: "Unrange",
            keybind: { keyCode: KeyCode.BACKSPACE },
            onClick: () => {
              unrangeItems(items);
            },
          },
          ...(canFlipHorizontally
            ? [
                {
                  label: "Flip Horizontally",
                  keybind: { keyCode: KeyCode.F },
                  onClick: () => {
                    flipItemsHorizontally(items.map((item) => item.uniqueId));
                  },
                },
              ]
            : []),
          ...(availableStores && items.length === 1
            ? [
                {
                  label: "Delete",
                  keybind: { keyCode: KeyCode.DELETE },
                  onClick: () => {
                    openPogDeleteProductModal({
                      item: items[0],
                      availableStores,
                    });
                  },
                },
              ]
            : []),
          ...(items.length === 1
            ? [
                {
                  label: "Side View",
                  keybind: { keyCode: KeyCode.S },
                  onClick: () => {
                    setSideView({
                      bayUniqueId: items[0].shelf.bay.uniqueId,
                      itemXLeft: items[0].getCoordinates().xLeft,
                      itemXRight: items[0].getCoordinates().xRight,
                    });
                  },
                },
              ]
            : []),
        ],
      },
    });
  };

  const onContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    const selectedShelfItems = currentShelfSelection();
    if (isPlanogramItem && !selectedShelfItems.find((selectedItem) => selectedItem === item)) {
      setSelectedItems([item]);
      openContextMenu(event, [item]);
    } else {
      openContextMenu(event, selectedShelfItems);
    }
  };

  const shelfType = isPlanogramItem && item.shelf !== null ? item.shelf.type : undefined;
  // A divided shelf spreads the facings: one divider stands after every facing column.
  const betweenFacingsDividerWidth =
    isPlanogramItem && item.shelf ? getBetweenFacingsDividerWidth(item.shelf.dividers) : 0;
  const horizontalFacings = item.getHorizontalFacings();
  const verticalFacings = item.getVerticalFacings();
  const { yCapWidth, yCapHeight, xCapWidth, xCapHeight, blockWidth, stackHeight } = drawn(
    [item],
    () => ({
      yCapWidth: item.getOrientedDimensionForOrientation("width", item.yCapOrientation),
      yCapHeight: item.getOrientedDimensionForOrientation("height", item.yCapOrientation),
      xCapWidth: item.getOrientedDimensionForOrientation("width", item.xCapOrientation),
      xCapHeight: item.getOrientedDimensionForOrientation("height", item.xCapOrientation),
      blockWidth: item.getWidth() * horizontalFacings,
      stackHeight: item.getHeight() * verticalFacings,
    }),
  );
  const yCapColumns = getCappingSpanCount(blockWidth, yCapWidth);
  const xCapRows = getCappingSpanCount(stackHeight, xCapHeight);

  const getDropperHeight = () => {
    if (!("shelf" in item)) {
      return 0;
    }

    if (
      item.shelf.getTopShelf()?.type === "HANGCELL" ||
      (item.shelf.type === "HANGCELL" && item.shelf.getBottomShelf()?.type !== "HANGCELL")
    ) {
      return item.shelf.getHeight() / 2;
    }

    return item.shelf.getHeight();
  };

  return (
    <ItemContainer ref={getRef()} shelfType={shelfType} onContextMenu={onContextMenu}>
      <DropperContainer shelfType={shelfType}>
        <Dropper
          side="static"
          width="auto"
          height={`calc(${getDropperHeight()}px * ${scaleY})`}
          onDropOver={onDropOverLeft}
          onDrop={onDropLeft}
          shelfType={shelfType}
        />
      </DropperContainer>

      <ItemsElement
        ref={refItem}
        tabIndex={-1}
        className="planogram-item"
        data-id={item.uniqueId}
        draggable={false}
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        isEditable={isEditable}
        isNotHighlighted={highlightsMode && !highlight}
      >
        {isSelected && <SelectionOutliner />}

        {isHighlighted &&
          (tone ? (
            <ToneHighlighter
              data-testid="item-issue-tone"
              $color={tone.color}
              $outline={!!tone.outline}
            />
          ) : (
            <Highlighter />
          ))}

        <CdtStudioItemPin uniqueId={item.uniqueId} />

        {item.yCapNum > 0 && (
          <VerticalCappingContainer
            $columns={yCapColumns}
            $rows={item.yCapNum}
            aria-label="vertical-capping-container"
            data-testid="vertical-capping-container"
          >
            {Array.from({ length: item.yCapNum * yCapColumns }).map((_cap, idx) => (
              <Facing
                key={`y-cap-${idx}`}
                item={item}
                scaleX={scaleX}
                scaleY={scaleY}
                showImage={renderMode !== "label-only"}
                overlays={overlays}
                showNewLabel={isNewInsertedItem}
                overrideOrientation={item.yCapOrientation}
                overrideWidth={yCapWidth}
                overrideHeight={yCapHeight}
                isCapped
                isCustomItem={isCustomItem}
              />
            ))}
          </VerticalCappingContainer>
        )}

        <MainRowContainer>
          <BaseGridElement
            item={item}
            $isSupportingCapping={item.yCapNum > 0}
            $columnGap={betweenFacingsDividerWidth * scaleX}
          >
            {Array.from(Array(item.facings).keys()).map((facing) => (
              <Facing
                key={facing}
                item={item}
                scaleX={scaleX}
                scaleY={scaleY}
                showImage={renderMode !== "label-only"}
                overlays={overlays}
                isCustomItem={isCustomItem}
                showNewLabel={isNewInsertedItem}
              />
            ))}
          </BaseGridElement>

          {item.xCapNum > 0 && (
            <HorizontalCappingContainer
              $columns={item.xCapNum}
              $rows={xCapRows}
              aria-label="horizontal-capping-container"
              data-testid="horizontal-capping-container"
            >
              {Array.from({ length: item.xCapNum * xCapRows }).map((_cap, idx) => (
                <Facing
                  key={`x-cap-${idx}`}
                  item={item}
                  scaleX={scaleX}
                  scaleY={scaleY}
                  showImage={renderMode !== "label-only"}
                  overlays={overlays}
                  showNewLabel={isNewInsertedItem}
                  overrideOrientation={item.xCapOrientation}
                  overrideWidth={xCapWidth}
                  overrideHeight={xCapHeight}
                  isCapped
                  isCustomItem={isCustomItem}
                />
              ))}
            </HorizontalCappingContainer>
          )}
        </MainRowContainer>

        {renderMode === "label-only" &&
          overlays
            .filter((o) => o.showOnLabelPlaceholder && !isCustomItem)
            .map((o) => (
              <OverlayWrapper key={o.id} $anchor={o.anchor} style={o.style}>
                {o.content}
              </OverlayWrapper>
            ))}

        {item.merchandisingStyle === "TRAY" && <Tray />}
        {isPlanogramItem && <FrontOverhangBadge marker={itemFrontOverhangMarker(item)} />}

        {isPlanogramItem && item.shelf && (
          <Dropper
            side="left"
            width="50%"
            height={`calc(${getDropperHeight()}px * ${scaleY})`}
            onDropOver={onDropOverLeft}
            onDrop={onDropLeft}
            shelfType={isPlanogramItem ? item.shelf.type : undefined}
          />
        )}

        {isPlanogramItem && item.shelf && (
          <Dropper
            side="right"
            width="50%"
            height={`calc(${getDropperHeight()}px * ${scaleY})`}
            onDropOver={onDropOverRight}
            onDrop={onDropRight}
            shelfType={isPlanogramItem ? item.shelf.type : undefined}
          />
        )}

        {highlight && <Highlight data-testid="item-highlight" highlightColor={highlight} />}
      </ItemsElement>
    </ItemContainer>
  );
};

export const Item = React.memo(ItemInner);
